import type { MatchState } from '../sim/state';
import type { SimEvent } from '../sim/events';
import type { SimConfig } from '../config/simConfig';
import type { PlayerInput } from '../types';
import type { TeamIdx } from '../types';
import { rngStep } from '../math/rng';
import { setPhase, enforceRestartClearance, enforceKickoffHalves, ballLive, startRestart, startKickoff, placePenalty, ceremonyBaseTicks } from './phase';
import { detectGoal } from './goals';
import { applyCelebration } from './celebration';
import { detectOutOfPlay } from './outOfPlay';
import { detectOffside } from './offside';
import { PITCH } from '../config/pitchConfig';

/** D65c: a red card is a TIMED suspension, not a full send-off — bring back any suspended player whose
 *  20 s (SUSPENSION_TICKS) has elapsed. He returns at his own-half bench-side touchline (inside the pitch,
 *  not the off-pitch bench), reset to a live stance; his RED card stays (discipline history). The keeper
 *  heir keeps the gloves — a returning keeper comes back as an outfielder (smallest-change rule). */
export function resolveSuspensions(state: MatchState, events: SimEvent[]): void {
  const pitch = PITCH[state.mode.teamSize];
  for (const p of state.players) {
    if (!p.sentOff || p.suspendedUntilTick <= 0 || state.tick < p.suspendedUntilTick) continue;
    p.sentOff = false;
    p.suspendedUntilTick = 0;
    const dir = state.attackDir[p.team];
    p.pos = {
      x: Math.max(-pitch.length / 2 + 0.5, Math.min(pitch.length / 2 - 0.5, -dir * pitch.length * 0.25)), // his own half
      y: -pitch.width / 2 + 0.5, // bench-side touchline, ON the pitch
    };
    p.vel = { x: 0, y: 0 };
    p.action = 'IDLE';
    p.actionTicks = 0;
    p.charge = null;
    events.push({ t: 'RETURN', entity: p.id });
  }
}

// Rules orchestrator (§6) — step 8 of the pipeline (after physics/possession, before finalize). Pure.
// PLAY: detect dead-ball conditions (goal now; out-of-play / trapped / offside / half-end land in later
// M3 slices). KICKOFF/RESTART_PENDING: hold placement legal, then go live on the taker's kick (§6.1).

export function applyRules(state: MatchState, inputs: readonly PlayerInput[], events: SimEvent[], cfg: SimConfig): void {
  void inputs;
  switch (state.phase) {
    case 'PLAY': {
      if (state.mode.offside && detectOffside(state, events, cfg)) return; // offside FOUL disallows the touch (§6.3)
      const goal = detectGoal(state, events, cfg);
      const out = !goal && detectOutOfPlay(state, events, cfg);
      const trapped = !goal && !out && detectTrapped(state, events, cfg);
      checkHalfEnd(state, events, cfg, goal || out || trapped); // §6.4 — end at the next dead ball / overrun cap
      // offside arrives in M3-S4.
      return;
    }
    case 'KICKOFF':
    case 'RESTART_PENDING': {
      // D122: the whistle is a thing you can hear. `phaseTicksLeft` is decremented at step 1, so the
      // equality below holds for exactly one tick — the tick the ceremony budget runs out and the ball
      // becomes takeable. The HUD's countdown ring lands on it; the SFX plays on it.
      if (state.restart !== null) {
        const ceremonyBase = ceremonyBaseTicks(state.phase, cfg) ?? cfg.rules.RESTART_MAX_TICKS; // D155: one rule, three readers
        if (state.phaseTicksLeft === ceremonyBase) events.push({ t: 'WHISTLE', kind: 'RESTART' });
      }
      // D103: the designated taker HOLDING the ball on the spot is not "in play". startKickoff hands him the
      // ball at the whistle (D43 — no loose-ball scramble), and `ballLive` returns true for ANY possessor, so
      // this branch flipped to PLAY on the kickoff's own first tick: the phase lasted ZERO ticks, D83's
      // ceremony never played for any kickoff, D34's kickoff-halves was unreachable, and two PHASE events
      // landed in one tick. The ball goes live when he PLAYS it — possession leaves him, or it starts moving.
      const takerHolds =
        state.restart !== null && state.restart.taker !== null && state.ball.possessor === state.restart.taker && Math.hypot(state.ball.vel.x, state.ball.vel.y) <= cfg.rules.TRAPPED_SPEED;
      if (ballLive(state, cfg) && !takerHolds) {
        state.restart = null;
        setPhase(state, 'PLAY', 0, events);
      } else if (state.restart?.kind === 'PENALTY') {
        placePenalty(state, cfg);
      } else {
        enforceRestartClearance(state, cfg);
        if (state.phase === 'KICKOFF') enforceKickoffHalves(state); // D34: everyone stays home until the kick
      }
      return;
    }
    case 'GOAL_CELEBRATION':
      applyCelebration(state, cfg); // D108 — mob the scorer / trudge back, for the whole ceremony
      return;
    default:
      return; // LOBBY / HALFTIME / FULLTIME — no in-tick rule work here
  }
}

/** Trapped free ball (slow, unpossessed, grounded for TRAPPED_TICKS) → DROP_BALL to the last team (§6.2). */
function detectTrapped(state: MatchState, events: SimEvent[], cfg: SimConfig): boolean {
  const b = state.ball;
  const speed = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
  if (b.possessor === null && speed < cfg.rules.TRAPPED_SPEED && b.pos.z < cfg.rules.TRAPPED_MAX_Z) b.trappedTicks += 1;
  else b.trappedTicks = 0;

  if (b.trappedTicks < cfg.rules.TRAPPED_TICKS) return false;
  const team: TeamIdx = b.lastTouch ? b.lastTouch.team : 0; // last-possession team
  startRestart(state, events, cfg, 'DROP_BALL', team, { x: b.pos.x, y: b.pos.y });
  return true;
}

/** End the half at the next dead ball once the clock passes `halfLengthTicks`, or forced at the +MAX_OVERRUN
 *  cap even mid-rally (§6.4). Overrides any restart the dead-ball detectors set. Golden goal ends it earlier. */
function checkHalfEnd(state: MatchState, events: SimEvent[], cfg: SimConfig, deadBall: boolean): void {
  if (state.phase !== 'PLAY' && state.phase !== 'GOAL_CELEBRATION' && state.phase !== 'RESTART_PENDING') return;
  const len = state.half === 3 ? cfg.rules.OT_LENGTH_TICKS : state.mode.halfLengthTicks;
  const over = state.clockTicks >= len;
  const forced = state.clockTicks >= len + cfg.rules.MAX_OVERRUN;
  if (!((deadBall && over) || forced)) return;
  endHalf(state, events, cfg);
}

function endHalf(state: MatchState, events: SimEvent[], cfg: SimConfig): void {
  state.restart = null;
  // D62: release the ball. A FORCED (MAX_OVERRUN) end fires mid-rally with no dead-ball detector having
  // run, and a recorded possessor starves the ball from both sides — step() skips stepBall while a
  // possessor is set, and skips resolvePossession outside PLAY — so the ball sat byte-identical at a
  // mid-play position for the whole 10 s HALFTIME window while its carrier ran away from it. detectGoal
  // already does exactly this before GOAL_CELEBRATION; endHalf was missing the equivalent.
  state.ball.possessor = null;
  state.ball.gkLock = null;
  state.ball.vel = { x: 0, y: 0, z: 0 };
  state.advantage = null; // no advantage survives the whistle (see referee/step.ts D62)
  if (state.half === 1) {
    state.attackDir = [(-state.attackDir[0]) as 1 | -1, (-state.attackDir[1]) as 1 | -1]; // sides swap (§6.4)
    events.push({ t: 'WHISTLE', kind: 'HALF_END' });
    setPhase(state, 'HALFTIME', cfg.rules.HALFTIME_TICKS, events);
    state.restart = { kind: 'KICKOFF', spot: { x: 0, y: 0 }, team: 1, taker: null }; // half 2 kickoff = the other team (taker set at startKickoff)
    return;
  }
  if (state.half === 2 && state.score[0] === state.score[1] && state.mode.overtime === 'GOLDEN_GOAL') {
    startOvertime(state, events, cfg); // §6.5
    return;
  }
  events.push({ t: 'WHISTLE', kind: 'FULL_END' });
  setPhase(state, 'FULLTIME', 0, events);
}

/** Golden-goal overtime (§6.5): half 3, clock reset, coin-flip kickoff via the sim RNG. */
function startOvertime(state: MatchState, events: SimEvent[], cfg: SimConfig): void {
  state.half = 3;
  state.clockTicks = 0;
  const r = rngStep(state.rng);
  state.rng = r.state;
  startKickoff(state, (r.value < 0.5 ? 0 : 1) as TeamIdx, events, cfg);
}
